<script setup>
import ModalPhone from '@/Components/ModalPhone.vue';
import { ref, computed, onMounted } from 'vue';

// Teleport рендерим только на клиенте — иначе SSR-гидрация ломает клик по кнопке
const mounted = ref(false);
const ymUid = ref(null);
onMounted(() => {
  mounted.value = true;
  try {
    const m = document.cookie.match(/(?:^|; )_ym_uid=([^;]+)/);
    if (m) ymUid.value = decodeURIComponent(m[1]);
  } catch (e) { /* noop */ }
});

const props = defineProps({
  lawyer: { type: Object, default: () => ({}) },
  serviceName: { type: String, default: null },
  uslDesc: { type: String, default: null },
  rating: { type: Number, default: null },
  reviewscount: { type: Number, default: null },
  phone: { type: String, default: null },
  phoneto: { type: String, default: null },
  backendurl: { type: String, default: null },
  useTrackingPhone: { type: Boolean, default: false },
  tracking: { type: Object, default: () => ({}) },
  label: { type: String, default: 'Показать телефон' },
  // Доп. цель Метрики на клик по кнопке «Показать телефон»
  revealGoal: { type: String, default: 'SHOW_PHONE' },
  // На десктопе кнопка белая: красная там уже стоит в боковой колонке,
  // а красный по дизайн-системе — одна кнопка на экран
  desktopSecondary: { type: Boolean, default: false },
});

const show = ref(false);

// Есть ли конкретный юрист (иначе — общий блок «консультация»)
const hasLawyer = computed(() => !!(props.lawyer && (props.lawyer.avatar_path || props.lawyer.name)));

// Номер виден, но последние две цифры скрыты звёздочками
const maskedPhone = computed(() => {
  const raw = props.phone ?? '';
  let count = 0;
  return raw.split('').reverse().map(ch => {
    if (/\d/.test(ch) && count < 2) { count++; return '*'; }
    return ch;
  }).reverse().join('');
});

function open() {
  try {
    // Своя цель кнопки «Показать телефон» (по умолчанию SHOW_PHONE — «Телефон на услугах»).
    // OPEN_PHONE здесь НЕ шлём, чтобы цели не пересекались: SHOW_PHONE учитывает
    // именно раскрытия через этот компонент, а OPEN_PHONE — все остальные места.
    if (props.revealGoal) window.goal(props.revealGoal);
    else window.goal('OPEN_PHONE');
  } catch (e) { /* noop */ }
  show.value = true;
}

const cleanHref = computed(() => {
  const raw = (props.phoneto ?? ('tel:' + (props.phone ?? ''))).replace(/^tel:/, '');
  const digits = raw.replace(/[^\d+]/g, '');
  return 'tel:' + digits;
});

// Мобильный клик: сразу набор номера, без модалки. Шлём PHONE_CLICK (ту же цель
// конверсии, что и при наборе из модалки) и серверный трекинг — как в ModalPhone.
function dialDirect() {
  try { window.goal('PHONE_CLICK'); } catch (e) { /* noop */ }
  try {
    if (props.tracking?.visit_uuid && props.backendurl) {
      const meta = document.querySelector('meta[name="csrf-token"]');
      fetch('/events/phone-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': meta ? meta.content : '' },
        body: JSON.stringify({ visit_uuid: props.tracking.visit_uuid, url: props.backendurl, ym_uid: ymUid.value }),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (e) { /* noop */ }
}

function stars(rating) {
  return Math.min(5, Math.max(0, Math.round(rating ?? 0)));
}

defineExpose({ open });
</script>

<template>
  <!-- Мобильный: сразу набор номера, без модалки -->
  <a
    :href="cleanHref"
    @click="dialDirect"
    class="reveal-btn lg:hidden inline-flex w-full h-full items-center gap-3 py-3 px-5 text-white
           bg-brand-600 hover:bg-brand-700 rounded-xl shadow-lg
           transition-colors duration-200 cursor-pointer justify-center
           focus:outline-none focus:ring-4 focus:ring-brand-200"
  >
    <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
    </svg>
    <div class="flex flex-col items-center">
      <span :class="useTrackingPhone ? 'roistat-phone' : ''" class="text-base font-bold whitespace-nowrap leading-tight">{{ phone }}</span>
      <span class="text-xs whitespace-nowrap">Позвонить</span>
    </div>
  </a>

  <!-- Десктоп: кнопка раскрывает модалку с инфо о юристе -->
  <button
    type="button"
    @click="open"
    class="reveal-btn hidden lg:inline-flex w-full h-full items-center gap-3 py-3 px-5 text-white
           bg-brand-600 hover:bg-brand-700 rounded-xl shadow-lg
           transition-colors duration-200 cursor-pointer justify-center
           focus:outline-none focus:ring-4 focus:ring-brand-200"
    :class="desktopSecondary ? 'reveal-btn--desk-2 lg:bg-white lg:hover:bg-gray-50 lg:text-gray-700 lg:border lg:border-gray-300 lg:shadow-none lg:rounded-lg lg:py-2.5 lg:focus:ring-indigo-200' : ''"
  >
    <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
    </svg>
    <div class="flex flex-col items-center">
      <span class="text-base font-bold whitespace-nowrap leading-tight">{{ maskedPhone }}</span>
      <span class="text-xs whitespace-nowrap">Показать телефон</span>
    </div>
  </button>

  <!-- Модалка с инфо о юристе и кликабельным телефоном -->
  <Teleport to="body" v-if="mounted">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="show"
        class="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 sm:p-4"
        @click.self="show = false"
      >
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-4"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-4"
        >
          <div v-if="show"
            class="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-y-auto max-h-[90dvh] sm:max-h-[88vh] pb-[env(safe-area-inset-bottom)] sm:pb-0"
          >
            <!-- Шапка -->
            <div class="relative bg-gradient-to-b from-brand-50 to-white pt-5 pb-3 px-5 flex flex-col items-center gap-1.5 text-center">
              <button
                class="absolute top-3 right-3 text-gray-500 hover:text-gray-600 transition-colors"
                @click="show = false"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>

              <!-- Конкретный юрист -->
              <template v-if="hasLawyer">
                <img
                  v-if="props.lawyer?.avatar_path"
                  :src="'https://nedicom.ru/' + props.lawyer.avatar_path"
                  :alt="props.lawyer.name"
                  class="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-lg"
                />
                <div>
                  <p class="text-lg font-bold text-gray-900">{{ props.lawyer?.name }}</p>
                  <p v-if="props.serviceName" class="text-sm text-indigo-600 font-medium mt-0.5">{{ props.serviceName }}</p>
                </div>
                <div v-if="props.rating" class="flex items-center gap-1.5 text-sm">
                  <span class="flex gap-0.5">
                    <svg v-for="i in 5" :key="i" class="w-4 h-4 shrink-0"
                      :class="i <= stars(props.rating) ? 'text-yellow-400' : 'text-gray-200'"
                      fill="currentColor" viewBox="0 0 22 20">
                      <use href="#ic-star" />
                    </svg>
                  </span>
                  <span class="font-semibold text-gray-900">{{ props.rating }}</span>
                  <span v-if="props.reviewscount" class="text-gray-500">· {{ props.reviewscount }} отзывов</span>
                </div>
                <p v-if="props.lawyer?.expirience"
                  class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-3 py-1">
                  <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Опыт работы: {{ props.lawyer.expirience }} лет
                </p>
              </template>

              <!-- Общая консультация (без конкретного юриста) -->
              <template v-else>
                <div class="w-14 h-14 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center shadow-sm">
                  <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-lg font-bold text-gray-900">Бесплатная консультация</p>
                  <p v-if="props.serviceName" class="text-sm text-indigo-600 font-medium mt-0.5">{{ props.serviceName }}</p>
                </div>
              </template>
            </div>

            <!-- Тело -->
            <div class="px-5 pb-4 pt-1 flex flex-col gap-3">
              <p v-if="props.uslDesc" class="text-xs text-gray-500 leading-snug text-center line-clamp-2">
                {{ props.uslDesc }}
              </p>

              <!-- Что вы получите -->
              <ul class="space-y-1.5">
                <li class="flex items-start gap-2 text-sm text-gray-600">
                  <svg class="w-4 h-4 mt-0.5 shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                  Бесплатно оценим перспективы вашего дела
                </li>
                <li class="flex items-start gap-2 text-sm text-gray-600">
                  <svg class="w-4 h-4 mt-0.5 shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                  Честно скажем о шансах — без ложных обещаний
                </li>
              </ul>

              <ModalPhone
                :phone="props.phone"
                :phoneto="props.phoneto"
                :backendurl="props.backendurl"
                :use-tracking-phone="props.useTrackingPhone"
                :tracking="props.tracking"
                goal="PHONE_CLICK"
              />

              <p class="text-xs text-gray-500 text-center leading-snug">
                Звонок бесплатный. Перезвоним, если не дозвонитесь.
              </p>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Пульс — того же красного, что и кнопка: голубое кольцо вокруг красной
   кнопки читалось как чужой элемент */
@keyframes pulse-ring {
  0%, 100% { box-shadow: 0 0 0 0 rgba(234, 0, 0, 0.45); }
  50%       { box-shadow: 0 0 0 10px rgba(234, 0, 0, 0); }
}
.reveal-btn {
  animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  box-shadow: 0 4px 16px rgba(234, 0, 0, 0.25);
}
@media (min-width: 1024px) {
  .reveal-btn--desk-2 { animation: none; box-shadow: none; }
}
@media (prefers-reduced-motion: reduce) {
  .reveal-btn { animation: none; }
}
</style>
